import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { ProductType } from '@pages/product-analyzer/Product';
import { getSubscriptionDetailsByProductType } from './AppSubscriptionUtils';
import {
  findCountryFromDefaultCurrenciesByCurrency,
  getCurrencyCode,
  getCurrencyOptionsFromSubscription,
} from './currencyUtils/CurrencyUtils';
import { sortOptionsFromApi } from './FilterSettings';
import { createToUpdateFilter, getAppliedFiltersFromPagesConfig, getInitialAppliedCountries } from './FilterUtils';
import { PagesConfig } from './pageConfig/PagesConfigContext';
import { byFilterKey } from './pageConfig/Predicates/SelectedFiltersPredicates';
import {
  Country,
  CountryFilterPair,
  CountryMasterPgsFilterPair,
  FilterKeys,
  FilterObject,
  Filters,
  FilterTitle,
  FilterTypes,
  Option,
  OptionPeriodicity,
  PageName,
  PageType,
  ProductGroupFeatureValue,
  TimePeriodPeriodicity,
} from './pageConfig/Types';
import { getDefaultValueForDerivablePeriodicity, getEndOfRangedPeriod } from './PeriodUtils';

export const updateCountryAndPGFilters = (
  pagesConfig: PagesConfig,
  selectedCountries: Option[],
  pgsByCountry: CountryFilterPair[],
  filter: FilterObject,
  productType: ProductType,
) => {
  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  const currentClientCountries = subscriptionDetails.currentClientCountries;
  const countryOptions = convertToCountrySelectorOptions(currentClientCountries);
  const pgOptions = getPgsByCountries(pgsByCountry, selectedCountries);

  const countryFilter = getCountryFilter(countryOptions);
  const pgFilter = getPgFilter(pgOptions);

  // Set base filter to side menu
  filter.filters = [countryFilter, pgFilter];

  // Use hitlistsClientId instead of clientId when the page is RI_PRODUCT
  const { pageName, clientId, hitlistsClientId } = pagesConfig;
  const effectiveClientId = pageName === PageName.RI_PRODUCT ? hitlistsClientId : clientId;
  const currencyOptions = getCurrencyOptionsFromSubscription(pagesConfig, productType, effectiveClientId);
  const currencyFilters = createToUpdateFilter(
    { filterKey: FilterKeys.CURRENCY, filterTitle: FilterTitle.CURRENCY },
    currencyOptions,
    false,
  );
  // Ordering of base filters are in the following order: Country, Currency, PG
  filter.filters.splice(1, 0, currencyFilters);

  pagesConfig.setPGsByCountry(pgsByCountry);
  pagesConfig.setPgListUpdated(true);
};

export const filterPGsByCountry = (pgsByCountry: CountryFilterPair[], countries: Option[]): CountryFilterPair[] => {
  if (!pgsByCountry || !countries) {
    return [];
  }

  return pgsByCountry.filter((p) => countries.map((c) => c.value).includes(p.country));
};

export const convertToCountrySelectorOptions = (countries: Country[] | any[]): Option[] => {
  let options: Option[] = countries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  return sortOptionsFromApi(options);
};

export const getPgsByCountries = (pgsByCountry: CountryFilterPair[], countries: Option[]) => {
  if (!countries || !pgsByCountry) {
    return [];
  }

  const countryNames = countries.map((c) => c.value);

  const combinedOptions = pgsByCountry.filter((p) => countryNames.includes(p.country)).flatMap((p) => p.filter.options);

  let options: Option[] = combinedOptions.filter(
    (option, i) => i === combinedOptions.findIndex((o) => o.value === option.value),
  );

  return sortOptionsFromApi(options);
};

/**
 * predicate to check if filterKey contains within the Filters array
 * @param filterKey (Market Definition filter keys eg. Online/Offline, Region and City)
 */
const filterKeyPredicate: (filterKey: FilterKeys) => (filter: Filters) => boolean = (filterKey) => (filter) =>
  filter.filterKey === filterKey;

/**
 * check whether the selected pg contains selected market definition filter keys (eg. Online/Offline, Region and City)
 * @param selectedFilters selected filters from side filter redux state
 * @param productGroupFeatureValueList product group feature value list from selected country
 * @param pg selected product group
 * @param filterKey market definition filter keys (eg. Online/Offline, Region and City)
 */
const isPGInvalidByFilterKey = (
  selectedFilters: Filters[],
  productGroupFeatureValueList: ProductGroupFeatureValue[],
  pg: Option,
  filterKey: FilterKeys,
): boolean => {
  const filterPredicate = filterKeyPredicate(filterKey);
  const hasFilter = selectedFilters?.find(filterPredicate);
  if (hasFilter) {
    const filteredProductGroupFeatureValues: string[] = productGroupFeatureValueList
      .filter((productGroupFeatureValue) => productGroupFeatureValue.productGroup === pg.value)
      .flatMap((productGroupFeatureValue) => productGroupFeatureValue.sideMenuFilterDtoList)
      .filter(filterPredicate)
      .flatMap((sideMenuFilter) => sideMenuFilter.options)
      .map((option) => option.value);

    if (hasFilter.filterTitle === FilterTypes.BRAND.title) {
      // Only consider the PG as invalid if there is selected brand, as there is possibility that
      // the selectedFilters has brand but with empty options (such as after clearing brands in
      // Trading Assistant page)
      const hasSelectedBrand = hasFilter.options && hasFilter.options.length > 0;
      return (
        hasSelectedBrand &&
        !selectedFilters
          .filter(filterPredicate)
          .flatMap((filter) => filter.options)
          .some((option) => filteredProductGroupFeatureValues.includes(option.value))
      );
    }

    return selectedFilters
      .filter(filterPredicate)
      .flatMap((filter) => filter.options)
      .some((option) => !filteredProductGroupFeatureValues.includes(option.value));
  }
  return false;
};

/**
 * Based on selected product groups,
 * get product groups which does not contain the selected filter keys for market definition
 * By default the filter keys are: Online/Offline, City, Region and Brand
 *
 * @param productGroup selected product group
 * @param productGroupFeatureValueList product group feature value list from selected country
 * @param selectedFilters selected side filters for market definition (eg. Online/Offline, Region and City)
 */
export const getInvalidPGFilterKeys = (
  productGroup: Option,
  productGroupFeatureValueList: ProductGroupFeatureValue[],
  selectedFilters: Filters[],
): FilterKeys[] => {
  return getInvalidPGBySelectedFilterKeys(productGroup, productGroupFeatureValueList, selectedFilters, [
    FilterKeys.ONLINE_OFFLINE,
    FilterKeys.CITY,
    FilterKeys.REGION,
    FilterKeys.BRAND,
    FilterKeys.CHANNEL,
  ]);
};

/**
 * Based on selected product groups,
 * get product groups which does not contain the selected filter keys for market definition
 *
 * @param productGroup selected product group
 * @param productGroupFeatureValueList product group feature value list from selected country
 * @param selectedFilters selected side filters for market definition (eg. Online/Offline, Region and City)
 * @param selectedFilterKeys selected filter keys to be checked
 */
export const getInvalidPGBySelectedFilterKeys = (
  productGroup: Option,
  productGroupFeatureValueList: ProductGroupFeatureValue[],
  selectedFilters: Filters[],
  selectedFilterKeys: FilterKeys[],
): FilterKeys[] => {
  if (!selectedFilters && !productGroupFeatureValueList) {
    return [];
  }

  if (!selectedProductGroupContainsInProductGroupFeatureValueList(productGroup, productGroupFeatureValueList)) {
    return [];
  }

  return selectedFilterKeys.filter((filterKey) =>
    isPGInvalidByFilterKey(selectedFilters, productGroupFeatureValueList, productGroup, filterKey),
  );
};

export const getInvalidPGs = (
  timeFrame: Periodicity,
  pgs: Option[],
  countries: Option[],
  pgsByCountry: CountryFilterPair[],
): Option[] => {
  if (!timeFrame || countries.length === 0 || pgsByCountry.length === 0) {
    return [];
  }

  const countryCodes = countries.map((c) => c.value);

  const availablePGs = pgsByCountry
    .filter(
      (p) =>
        p.periodType &&
        countryCodes.includes(p.country) &&
        getDefaultValueForDerivablePeriodicity(timeFrame).toUpperCase() === p.periodType.toUpperCase(),
    )
    .flatMap((p) => p.filter.options)
    .map((o) => o.value);

  return pgs.filter((pg) => !availablePGs.includes(pg.value));
};

export const getCountryFilter = (options: Option[]) => {
  return {
    dataType: 'COUNTRY',
    feature: 'COUNTRY',
    filterTitle: 'Country',
    filterKey: FilterKeys.COUNTRY,
    options: options,
    disabled: false,
  };
};

export const getPgFilter = (options: Option[]) => {
  return {
    dataType: 'PRODUCT GROUP',
    feature: 'PRODUCT GROUP',
    filterTitle: 'Product Group',
    filterKey: FilterKeys.PRODUCT_GROUP,
    options: options,
    disabled: false,
  };
};

/**
 * check whether the selected product group exist in productGroupFeatureValueList
 * @param productGroup selected product group
 * @param productGroupFeatureValueList productGroupFeatureValueList based on country and time frame
 */
const selectedProductGroupContainsInProductGroupFeatureValueList = (
  productGroup: Option,
  productGroupFeatureValueList: ProductGroupFeatureValue[],
): boolean => {
  if (!productGroupFeatureValueList || productGroupFeatureValueList.length === 0) return false;

  return productGroupFeatureValueList.some((pg) => pg.productGroup === productGroup.value);
};

/**
 *
 * @param pgsByCountry is the country scope metadata
 * @param timeFrame is the selected timeframe
 * returns monthly if timeframe is Calendar Quarter or Rolling Quarter
 * else return default timeframe
 */
export const getCountryFilterPairsByTimeFrame: (
  pgsByCountry: CountryFilterPair[],
  timeFrame: Periodicity,
) => CountryFilterPair[] = (pgsByCountry, timeFrame) => {
  let periodicityToCompare: string;
  if (timeFrame === Periodicity.CustomWeeks) {
    periodicityToCompare = Periodicity.Weekly;
  } else if ([Periodicity.CalendarQuarter, Periodicity.RollingQuarter, Periodicity.CustomMonths].includes(timeFrame)) {
    periodicityToCompare = Periodicity.Monthly;
  } else {
    periodicityToCompare = timeFrame;
  }

  return pgsByCountry.filter((pgs) => pgs.periodType === periodicityToCompare?.toUpperCase());
};

export const appliedCountry = (
  pagesConfig: PagesConfig,
  pageType: PageType,
  clientCountries: Country[],
  productType: ProductType,
): Country | undefined => {
  let appliedCountries = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, pageType);
  appliedCountries ??= getInitialAppliedCountries(pagesConfig, pageType, productType);
  return clientCountries
    .filter((clientCountry) => appliedCountries.some((country) => clientCountry.id === country.value))
    .slice(-1)[0];
};

/**
 * Construct a country option object to be used as a filter option in side menu filter.
 *
 * @param country - The country object to be converted.
 */
const constructCountryOption = (country: Country): Option => ({
  label: country.name,
  value: country.id,
});

/**
 * Returns the list of country option based on the selected currency filter.
 *
 * @param pagesConfig - A reference to pagesConfig.
 * @param effectiveProductType - The current active product type.
 * @param currencyFilter - The currency filter. It can be from selected or applied filters.
 */
export const getCountryListByCurrency = (
  pagesConfig: PagesConfig,
  effectiveProductType: ProductType,
  currencyFilter: Filters,
): Option[] => {
  // Retrieve a list of country options based on subscription
  const { currentClientCountries } = getSubscriptionDetailsByProductType(effectiveProductType, pagesConfig);

  const countryListByCurrency = currentClientCountries.filter(
    (clientCountry) => clientCountry.currency === getCurrencyCode(currencyFilter),
  );
  if (countryListByCurrency && countryListByCurrency.length > 0) {
    return countryListByCurrency.map((countryByCurrency) => constructCountryOption(countryByCurrency));
  }

  // If its not from the subscription country currencies, then its definitely from the default
  // currencies (i.e. USD or EUR) since the default ones are always displayed.
  // Safe to assume that it will always be found (i.e. not null)
  const country = findCountryFromDefaultCurrenciesByCurrency(currencyFilter)!;
  return [
    {
      label: country.countryName,
      value: country.countryCode,
    },
  ];
};

/**
 * Returns the list of country option based on the accessible product groups.
 *
 * @param appliedCountries - List of applied countries
 * @param appliedProductGroups - List of applied product groups
 * @param pagesConfig - A reference to pagesConfig.
 * @param periodicity - periodicity
 */
export const getCountriesByAccessibleProductGroup = (
  appliedCountries: Option[],
  appliedProductGroups: Option[],
  pagesConfig: PagesConfig,
  periodicity: Periodicity,
): Option[] => {
  if (appliedCountries.length === 0 || appliedProductGroups?.length === 0) {
    return appliedCountries;
  }
  const countriesByAccessibleProductGroup: Option[] = [];
  appliedCountries.forEach((currentCountry) => {
    const countryAccessibleProductGroups = pagesConfig.pgsByCountry.find(
      ({ country, periodType }) =>
        country === currentCountry.value &&
        periodType === getDefaultValueForDerivablePeriodicity(periodicity).toUpperCase(),
    );
    if (countryAccessibleProductGroups) {
      if (
        countryAccessibleProductGroups.filter.options.some((option) => option.value === appliedProductGroups?.[0].value)
      ) {
        countriesByAccessibleProductGroup.push(currentCountry);
      }
    }
  });
  return countriesByAccessibleProductGroup;
};

export const getAppliedCountry = (pagesConfig: PagesConfig): Option[] => {
  const country = pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters.find(byFilterKey(FilterKeys.COUNTRY))
    ?.options[0];
  return country
    ? [
        {
          label: country.label,
          value: country.value,
        },
      ]
    : [
        {
          label: '',
          value: '',
        },
      ];
};

export const getCountryWithMissingPgs = (
  timeFrame: Periodicity,
  appliedProductGroups: Option[],
  appliedCountries: Option[],
  pgListByCountry: CountryFilterPair[],
  showWarning: boolean = false,
): Option[] => {
  if (
    !timeFrame ||
    appliedCountries.length === 0 ||
    pgListByCountry.length === 0 ||
    appliedProductGroups.length === 0
  ) {
    return [];
  }
  //filter by the selected period
  const pgByCountryByPeriodType = pgListByCountry.filter(
    (pgByCountry) =>
      pgByCountry.periodType &&
      getDefaultValueForDerivablePeriodicity(timeFrame).toUpperCase() === pgByCountry.periodType.toUpperCase(),
  );

  //Filter by the pg that doesnt exist
  const countriesWithNoAppliedPgs = pgByCountryByPeriodType
    .filter((countryFilterPair) =>
      appliedProductGroups.some(
        (pg) => !countryFilterPair.filter.options.some((pgByCountry) => pgByCountry.value === pg.value),
      ),
    )
    .map((filteredCountryFilterPair) => filteredCountryFilterPair.country);
  //Countries that does not exist for selected period type
  //Eg: PE has weekly and monthly, DE has only monthly,
  //but selected periodType is Weekly.
  //Hence countriesNotInPeriod should contain ['DE']
  const countriesNotInPeriod = appliedCountries
    .filter((country) => !pgByCountryByPeriodType.some((pgByCountry) => pgByCountry.country === country.value))
    .map((country) => country.value);
  return appliedCountries.filter((country) =>
    showWarning
      ? countriesWithNoAppliedPgs.includes(country.value) || countriesNotInPeriod.includes(country.value)
      : countriesWithNoAppliedPgs.includes(country.value) && countriesNotInPeriod.includes(country.value),
  );
};
const getMasterPgsByTimeFrame = (
  countryData: CountryMasterPgsFilterPair[],
  timeFrame: Periodicity,
  formattedPeriodValue: string,
): OptionPeriodicity[] => {
  const monthlyData = countryData.flatMap((pg) => pg.timePeriod.monthly ?? []);
  const weeklyData = countryData.flatMap((pg) => pg.timePeriod.weekly ?? []);

  switch (timeFrame) {
    case Periodicity.Monthly:
    case Periodicity.RollingQuarter:
      return monthlyData;
    case Periodicity.Weekly:
      return weeklyData;
    case Periodicity.YTD:
    case Periodicity.MAT: {
      const key: keyof TimePeriodPeriodicity = formattedPeriodValue.includes('Week') ? 'weekly' : 'monthly';
      return countryData.flatMap((pg) => pg.timePeriod[key] ?? []);
    }
    case Periodicity.CalendarQuarter:
    case Periodicity.CustomMonths: {
      return monthlyData.some((month) => month.value.includes(formattedPeriodValue)) ? monthlyData : [];
    }
    default:
      return [];
  }
};

const getMasterPgsByPeriod = (masterPgsByTimeFrame: OptionPeriodicity[], formattedPeriodValue: string): Option[] => {
  return masterPgsByTimeFrame
    .filter((availablePgByTF) => availablePgByTF.value.includes(formattedPeriodValue))
    .flatMap((pg) => pg.availablePgs);
};

export const getUnavailableCountryByPGsByPeriod = (
  periodValue: string,
  timeFrame: Periodicity,
  masterPgsByCountry: CountryMasterPgsFilterPair[],
  subscribeCountries?: Option[],
  productGroups?: Option[],
): string[] => {
  if (!subscribeCountries || !productGroups || periodValue.length === 0) {
    return [];
  }

  return subscribeCountries
    .filter((subscribedCountry) => {
      const countryData = masterPgsByCountry.filter((pg) => subscribedCountry.value.includes(pg.country));

      // Updates the periodValue to take the furthest period in the range.
      // Eg: Jan 2024 - Dec 2024, it will take Dec 2024
      const formattedPeriodValue = getEndOfRangedPeriod(periodValue, timeFrame);
      const masterPgsByTimeFrame = getMasterPgsByTimeFrame(countryData, timeFrame, formattedPeriodValue);
      if (masterPgsByTimeFrame.length === 0) {
        return false;
      }

      const masterPgsByPeriod = getMasterPgsByPeriod(masterPgsByTimeFrame, formattedPeriodValue);
      if (productGroups.length === 0) {
        return masterPgsByPeriod.length === 0;
      }

      const pgs = productGroups.map((pg) => pg.value);
      return masterPgsByPeriod.filter((pg) => pgs.includes(pg.value)).length === 0;
    })
    .map((country) => country.value);
};
/**Filter the appliedCountries list to return only subscribe country list**/
export const getSubscribeCountries = (
  timeFrame: Periodicity,
  countryBySubscription: CountryFilterPair[],
  appliedCountries?: Option[],
  appliedProductGroups?: Option[],
): Option[] => {
  if (!appliedCountries || !timeFrame || appliedCountries.length === 0 || countryBySubscription.length === 0) {
    return [];
  }
  let subscribeCountryArray: Option[] = [];
  const countryByPeriodType = countryBySubscription.filter(
    (p) =>
      p.periodType && getDefaultValueForDerivablePeriodicity(timeFrame).toUpperCase() === p.periodType.toUpperCase(),
  );

  appliedCountries.forEach((c) => {
    const countryData = countryByPeriodType.filter((pg) => c.value.includes(pg.country));

    let masterPgsByPeriod = countryData.flatMap((pg) => pg?.filter.options);

    let availableMasterPgs: any[];
    if (!appliedProductGroups || appliedProductGroups.length === 0) {
      availableMasterPgs = masterPgsByPeriod.map((pg) => pg?.value);
    } else {
      const pgs = appliedProductGroups.map((pg) => pg.value);
      availableMasterPgs = masterPgsByPeriod.filter((pg) => {
        return !!pg && pgs.includes(pg.value);
      });
    }

    if (availableMasterPgs.length > 0) {
      subscribeCountryArray.push({ value: c.value, label: c.label });
    }
  });

  return subscribeCountryArray;
};

export const getCountryOptions = (countries: Country[]): Option[] =>
  countries.map((country) => ({
    label: country.name,
    value: country.id,
  }));

export const getCountryCodes = (countries: Country[]): string[] => countries.map((country) => country.id);

export const getCountryIdsByCountryNames = (countryNames: string[], countries: Country[]) => {
  return countries.filter((country) => countryNames.includes(country.name)).map((country) => country.id);
};
